import { ART_PAL, ball, body, edge, fade, gloss, mix, n, shade, shadowInk, star, tint, warm, type ArtPal, type ArtTheme } from './covers/kit';

/*
 * Small production art (same "premium casual" style as the covers): trophy, medals 1–3 (places shown by pips,
 * no digits), level ring, streak (chevrons, no fire), gift, provably-fair shield, logo mark, and three calm
 * empty-state illustrations. Every builder takes the palette and a defs-id prefix.
 */
export type MiscName =
  | 'trophy'
  | 'medal-1'
  | 'medal-2'
  | 'medal-3'
  | 'level-ring'
  | 'streak'
  | 'gift'
  | 'shield'
  | 'logo'
  | 'empty-history'
  | 'no-connection'
  | 'not-found';

/** medal / trophy gold: the light-theme gold is tinted more so 1st place never reads as bronze */
const gold1 = (P: ArtPal) => (P.dark ? P.gold : tint(P.gold, 0.5));
const silver = (P: ArtPal) => (P.dark ? mix(P.ink, '#8A96AC', 0.35) : mix('#C9D2E0', '#9AA5B8', 0.4));
const bronze = (P: ArtPal) => mix(P.orange, P.dark ? '#8A4B22' : '#7A3E14', 0.35);

function trophy(P: ArtPal, p: string): string {
  const g = gold1(P);
  const base = P.dark ? mix(P.purple, P.bg, 0.35) : P.purple;
  return (
    `<defs>${body(`${p}c`, g, 0.45, 0.4, 0.5, 1)}${body(`${p}b`, base, 0.35, 0.35, 0.4, 1)}${fade(`${p}s`, shadowInk(P), P.dark ? 0.5 : 0.18)}</defs>` +
    `<ellipse cx="64" cy="118" rx="40" ry="6" fill="url(#${p}s)"/>` +
    // handles
    `<path d="M34 30H18Q12 30 13 38Q16 60 40 64" fill="none" stroke="${edge(g, P)}" stroke-width="9" stroke-linecap="round"/>` +
    `<path d="M34 30H18Q12 30 13 38Q16 60 40 64" fill="none" stroke="${g}" stroke-width="5" stroke-linecap="round"/>` +
    `<path d="M94 30H110Q116 30 115 38Q112 60 88 64" fill="none" stroke="${edge(g, P)}" stroke-width="9" stroke-linecap="round"/>` +
    `<path d="M94 30H110Q116 30 115 38Q112 60 88 64" fill="none" stroke="${g}" stroke-width="5" stroke-linecap="round"/>` +
    // cup
    `<path d="M30 18H98V36C98 60 84 76 64 78C44 76 30 60 30 36Z" fill="url(#${p}c)" stroke="${edge(g, P)}" stroke-width="2.5" stroke-linejoin="round"/>` +
    `<polygon points="${star(64, 44, 15, 6, 4)}" fill="#FFFFFF" fill-opacity=".75"/>` +
    // stem + base
    `<path d="M56 78H72L70 94H58Z" fill="url(#${p}c)" stroke="${edge(g, P)}" stroke-width="2"/>` +
    `<rect x="36" y="94" width="56" height="20" rx="4" fill="url(#${p}b)" stroke="${edge(base, P)}" stroke-width="2"/>` +
    `<rect x="48" y="100" width="32" height="7" rx="3.5" fill="${warm(P)}" fill-opacity=".8"/>` +
    gloss(44, 30, 9, 4, -20, 0.6)
  );
}

function medal(P: ArtPal, p: string, place: 1 | 2 | 3): string {
  const c = place === 1 ? gold1(P) : place === 2 ? silver(P) : bronze(P);
  const rA = place === 1 ? P.teal : place === 2 ? P.blue : P.purple;
  const rB = shade(rA, 0.25);
  const pips = [
    [[64, 84]],
    [
      [64, 76],
      [64, 92],
    ],
    [
      [64, 76],
      [55, 90],
      [73, 90],
    ],
  ][place - 1] as number[][];
  return (
    `<defs>${ball(`${p}d`, c, 0.5, 0.4)}${ball(`${p}i`, c, 0.25, 0.25)}${fade(`${p}s`, shadowInk(P), P.dark ? 0.45 : 0.16)}</defs>` +
    `<ellipse cx="64" cy="122" rx="30" ry="4.5" fill="url(#${p}s)"/>` +
    // ribbon (V)
    `<path d="M38 6H58L74 52H54Z" fill="${rA}" stroke="${edge(rA, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M90 6H70L54 52H74Z" fill="${rB}" stroke="${edge(rA, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M44 8H54" stroke="#FFFFFF" stroke-opacity=".45" stroke-width="3" stroke-linecap="round"/>` +
    // disc
    `<circle cx="64" cy="84" r="34" fill="url(#${p}d)" stroke="${edge(c, P)}" stroke-width="2.5"/>` +
    `<circle cx="64" cy="84" r="24" fill="url(#${p}i)" stroke="${edge(c, P)}" stroke-width="2" stroke-opacity=".6"/>` +
    pips.map(([x, y]) => `<circle cx="${x}" cy="${y}" r="5" fill="#FFFFFF" fill-opacity=".85" stroke="${edge(c, P)}" stroke-width="1.2"/>`).join('') +
    gloss(50, 66, 10, 4.5, -35, 0.6)
  );
}

function levelRing(P: ArtPal, p: string): string {
  const ticks: string[] = [];
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * Math.PI * 2 - Math.PI / 2;
    ticks.push(`M${n(64 + 53 * Math.cos(a))} ${n(64 + 53 * Math.sin(a))}L${n(64 + 57 * Math.cos(a))} ${n(64 + 57 * Math.sin(a))}`);
  }
  return (
    `<defs><linearGradient id="${p}r" x1=".1" y1="0" x2=".9" y2="1"><stop offset="0" stop-color="${tint(P.teal, 0.45)}"/><stop offset=".45" stop-color="${P.teal}"/><stop offset=".8" stop-color="${P.blue}"/><stop offset="1" stop-color="${shade(P.blue, 0.3)}"/></linearGradient></defs>` +
    `<circle cx="64" cy="64" r="46" fill="none" stroke="${shade(P.blue, P.dark ? 0.55 : 0.1)}" stroke-opacity=".5" stroke-width="16"/>` +
    `<circle cx="64" cy="64" r="46" fill="none" stroke="url(#${p}r)" stroke-width="12"/>` +
    `<path d="M30 40A42 42 0 0 1 58 22" fill="none" stroke="#FFFFFF" stroke-opacity=".55" stroke-width="4" stroke-linecap="round"/>` +
    `<path d="${ticks.join('')}" stroke="${P.dark ? P.ink : P.blue}" stroke-opacity=".35" stroke-width="2" stroke-linecap="round"/>` +
    `<polygon points="${star(64, 10, 9, 3.6, 4)}" fill="${warm(P)}" stroke="${edge(warm(P), P)}" stroke-width="1.2"/>`
  );
}

function streak(P: ArtPal, p: string): string {
  const chev = (y: number, o: number) =>
    `<path d="M28 ${y + 22}L64 ${y}L100 ${y + 22}L100 ${y + 38}L64 ${y + 16}L28 ${y + 38}Z" fill="url(#${p}c)" fill-opacity="${o}" stroke="${edge(P.orange, P)}" stroke-width="2" stroke-linejoin="round"/>`;
  return (
    `<defs><linearGradient id="${p}c" x1=".2" y1="0" x2=".6" y2="1"><stop offset="0" stop-color="${tint(warm(P), 0.35)}"/><stop offset=".45" stop-color="${warm(P)}"/><stop offset="1" stop-color="${P.orange}"/></linearGradient>${fade(`${p}s`, shadowInk(P), P.dark ? 0.45 : 0.16)}</defs>` +
    `<ellipse cx="64" cy="118" rx="36" ry="5" fill="url(#${p}s)"/>` +
    chev(66, 0.55) +
    chev(42, 0.8) +
    chev(18, 1) +
    gloss(48, 34, 12, 2.5, -31, 0.55)
  );
}

function gift(P: ArtPal, p: string): string {
  const box = P.pink;
  const rib = warm(P);
  return (
    `<defs>${body(`${p}b`, box, 0.35, 0.35, 0.5, 1)}${body(`${p}l`, box, 0.5, 0.25, 0.5, 1)}${body(`${p}r`, rib, 0.4, 0.3, 0.5, 1)}${fade(`${p}s`, shadowInk(P), P.dark ? 0.5 : 0.18)}</defs>` +
    `<ellipse cx="64" cy="118" rx="46" ry="6" fill="url(#${p}s)"/>` +
    // bow
    `<path d="M64 36C50 16 30 18 34 30C37 40 54 38 64 36Z" fill="url(#${p}r)" stroke="${edge(rib, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M64 36C78 16 98 18 94 30C91 40 74 38 64 36Z" fill="url(#${p}r)" stroke="${edge(rib, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    // box + lid
    `<rect x="24" y="56" width="80" height="58" rx="6" fill="url(#${p}b)" stroke="${edge(box, P)}" stroke-width="2.5"/>` +
    `<rect x="18" y="38" width="92" height="22" rx="6" fill="url(#${p}l)" stroke="${edge(box, P)}" stroke-width="2.5"/>` +
    `<rect x="56" y="38" width="16" height="76" fill="url(#${p}r)" stroke="${edge(rib, P)}" stroke-width="1.5"/>` +
    `<circle cx="64" cy="37" r="7" fill="url(#${p}r)" stroke="${edge(rib, P)}" stroke-width="1.8"/>` +
    gloss(36, 44, 12, 3, -6, 0.6) +
    `<path d="M32 66V104" stroke="#FFFFFF" stroke-opacity=".25" stroke-width="4" stroke-linecap="round"/>`
  );
}

function shield(P: ArtPal, p: string): string {
  return (
    `<defs>${body(`${p}s`, P.teal, 0.45, 0.4, 0.6, 1)}</defs>` +
    `<path d="M32 4L56 12V30C56 45 46 55 32 60C18 55 8 45 8 30V12Z" fill="url(#${p}s)" stroke="${edge(P.teal, P)}" stroke-width="2.5" stroke-linejoin="round"/>` +
    `<path d="M14 16L32 10" stroke="#FFFFFF" stroke-opacity=".55" stroke-width="3" stroke-linecap="round"/>` +
    `<path d="M20 32L29 41L45 23" fill="none" stroke="${P.dark ? P.bg : '#FFFFFF'}" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>`
  );
}

/**
 * Leyo · 乐游 mark — a kite (a square turned 45°, like the square corners of the UI), tilted by 8°: two teal and two
 * orange panels, a white cross spar, an edge a tone darker, a gloss top-left. Same drawing as design/brand/leyo
 * (sketch: design/brand/build.mjs), without the tail and the shadow and scaled to fill the square — it lives at 16–24 px
 * next to the name, so the spar and the edge are a little heavier than on the 128 px sketch.
 */
function logo(P: ArtPal, p: string): string {
  const T = [64, 8], R = [104, 46], B = [64, 96], L = [24, 46], C = [64, 46];
  const tri = (a: number[], b: number[], id: string) => `<path d="M${C}L${a}L${b}Z" fill="url(#${p}${id})"/>`;
  return (
    `<defs>${body(`${p}a`, P.teal, 0.5, 0.25, 0.7, 1)}${body(`${p}b`, P.orange, 0.35, 0.3, 0.6, 1)}` +
    `${body(`${p}c`, P.orange, 0.1, 0.45, 0.6, 1)}${body(`${p}d`, P.teal, 0.05, 0.5, 0.6, 1)}</defs>` +
    `<g transform="translate(64 64) scale(1.2) translate(-64 -52)"><g transform="rotate(-8 64 52)">` +
    tri(T, L, 'a') + tri(T, R, 'b') + tri(L, B, 'c') + tri(R, B, 'd') +
    `<path d="M${T}L${B}M${L}L${R}" stroke="#FFFFFF" stroke-opacity=".85" stroke-width="4" stroke-linecap="round"/>` +
    `<path d="M${T}L${R}L${B}L${L}Z" fill="none" stroke="${edge(P.teal, P)}" stroke-width="4.5" stroke-linejoin="round"/>` +
    gloss(46, 30, 12, 4, -43, 0.5) +
    `</g></g>`
  );
}

/* ---------------- empty states (320×240) ---------------- */

function scene(P: ArtPal, p: string, inner: string, defs: string, accent: string): string {
  return (
    `<defs>${fade(`${p}g`, accent, P.dark ? 0.16 : 0.2)}${fade(`${p}s`, shadowInk(P), P.dark ? 0.5 : 0.16)}${defs}</defs>` +
    `<ellipse cx="160" cy="116" rx="130" ry="104" fill="url(#${p}g)"/>` +
    `<ellipse cx="160" cy="210" rx="96" ry="10" fill="url(#${p}s)"/>` +
    inner
  );
}

function emptyHistory(P: ArtPal, p: string): string {
  const paper = P.dark ? mix(P.panel, P.ink, 0.2) : '#FFFFFF';
  const lines = [0, 1, 2, 3]
    .map((i) => `<rect x="130" y="${86 + i * 24}" width="${i === 3 ? 40 : 70}" height="9" rx="4.5" fill="${P.dark ? P.ink : P.ink}" fill-opacity="${P.dark ? 0.12 : 0.08}"/><circle cx="116" cy="${90.5 + i * 24}" r="5" fill="none" stroke="${P.teal}" stroke-opacity=".5" stroke-width="2"/>`)
    .join('');
  return scene(
    P,
    p,
    `<rect x="94" y="46" width="132" height="160" rx="10" fill="url(#${p}p)" stroke="${edge(paper, P)}" stroke-width="2"/>` +
      `<rect x="128" y="36" width="64" height="22" rx="8" fill="url(#${p}c)" stroke="${edge(P.teal, P)}" stroke-width="2"/>` +
      lines +
      gloss(118, 60, 18, 3, -8, P.dark ? 0.12 : 0.8) +
      `<polygon points="${star(236, 62, 12, 4.5, 4)}" fill="${warm(P)}"/><polygon points="${star(78, 150, 8, 3, 4)}" fill="${P.teal}" fill-opacity=".7"/>`,
    body(`${p}p`, paper, 0.3, P.dark ? 0.3 : 0.06, 0.4, 1) + body(`${p}c`, P.teal, 0.4, 0.3, 0.4, 1),
    P.teal,
  );
}

function noConnection(P: ArtPal, p: string): string {
  const plug = P.dark ? mix(P.panel, P.ink, 0.28) : '#FFFFFF';
  return scene(
    P,
    p,
    // cable left
    `<path d="M20 170C60 170 70 128 104 128" fill="none" stroke="${P.dark ? mix(P.ink, P.bg, 0.5) : '#9AA5B8'}" stroke-width="8" stroke-linecap="round"/>` +
      `<path d="M300 170C260 170 250 128 216 128" fill="none" stroke="${P.dark ? mix(P.ink, P.bg, 0.5) : '#9AA5B8'}" stroke-width="8" stroke-linecap="round"/>` +
      // plug (left) with prongs
      `<rect x="100" y="104" width="44" height="48" rx="10" fill="url(#${p}a)" stroke="${edge(plug, P)}" stroke-width="2"/>` +
      `<rect x="144" y="114" width="14" height="7" rx="3" fill="${warm(P)}"/><rect x="144" y="135" width="14" height="7" rx="3" fill="${warm(P)}"/>` +
      // socket (right)
      `<rect x="176" y="104" width="44" height="48" rx="10" fill="url(#${p}a)" stroke="${edge(plug, P)}" stroke-width="2"/>` +
      `<rect x="180" y="114" width="10" height="7" rx="3" fill="${P.dark ? P.bg : shade(P.bg, 0.15)}"/><rect x="180" y="135" width="10" height="7" rx="3" fill="${P.dark ? P.bg : shade(P.bg, 0.15)}"/>` +
      gloss(114, 112, 9, 3, -20, P.dark ? 0.2 : 0.8) +
      gloss(190, 112, 9, 3, -20, P.dark ? 0.2 : 0.8) +
      // small calm sparks in the gap
      `<path d="M166 96L162 88M166 160L162 168M170 128H176" stroke="${P.orange}" stroke-width="3" stroke-linecap="round" stroke-opacity=".8"/>`,
    body(`${p}a`, plug, 0.35, P.dark ? 0.3 : 0.08, 0.4, 1),
    P.orange,
  );
}

function notFound(P: ArtPal, p: string): string {
  const glass = P.sky;
  const dots: string[] = [];
  for (let i = 0; i < 22; i++) {
    const a = (i / 22) * Math.PI * 2;
    dots.push(`<circle cx="${n(150 + 92 * Math.cos(a))}" cy="${n(118 + 36 * Math.sin(a))}" r="2" fill="${P.dark ? P.ink : P.blue}" fill-opacity=".35"/>`);
  }
  return scene(
    P,
    p,
    dots.join('') +
      `<circle cx="228" cy="92" r="16" fill="url(#${p}m)" stroke="${edge(P.purple, P)}" stroke-width="2"/>` +
      gloss(222, 86, 5, 3, -35, 0.6) +
      `<polygon points="${star(84, 70, 9, 3.4, 4)}" fill="${warm(P)}"/><polygon points="${star(250, 170, 6, 2.4, 4)}" fill="${P.teal}"/>` +
      // magnifier
      `<rect x="168" y="150" width="54" height="16" rx="8" transform="rotate(40 168 150)" fill="url(#${p}h)" stroke="${edge(P.orange, P)}" stroke-width="2"/>` +
      `<circle cx="140" cy="118" r="44" fill="${glass}" fill-opacity="${P.dark ? 0.14 : 0.16}" stroke="url(#${p}f)" stroke-width="12"/>` +
      `<path d="M112 100A32 32 0 0 1 134 82" fill="none" stroke="#FFFFFF" stroke-opacity=".6" stroke-width="5" stroke-linecap="round"/>`,
    ball(`${p}m`, P.purple, 0.45, 0.4) +
      body(`${p}h`, P.orange, 0.4, 0.35, 0.3, 1) +
      `<linearGradient id="${p}f" x1=".15" y1="0" x2=".8" y2="1"><stop offset="0" stop-color="${tint(P.blue, 0.4)}"/><stop offset=".5" stop-color="${P.blue}"/><stop offset="1" stop-color="${shade(P.blue, 0.35)}"/></linearGradient>`,
    P.blue,
  );
}

const BUILD: Record<MiscName, { w: number; h: number; b: (P: ArtPal, p: string) => string }> = {
  trophy: { w: 128, h: 128, b: trophy },
  'medal-1': { w: 128, h: 128, b: (P, p) => medal(P, p, 1) },
  'medal-2': { w: 128, h: 128, b: (P, p) => medal(P, p, 2) },
  'medal-3': { w: 128, h: 128, b: (P, p) => medal(P, p, 3) },
  'level-ring': { w: 128, h: 128, b: levelRing },
  streak: { w: 128, h: 128, b: streak },
  gift: { w: 128, h: 128, b: gift },
  shield: { w: 64, h: 64, b: shield },
  logo: { w: 128, h: 128, b: logo },
  'empty-history': { w: 320, h: 240, b: emptyHistory },
  'no-connection': { w: 320, h: 240, b: noConnection },
  'not-found': { w: 320, h: 240, b: notFound },
};

export const MISC_NAMES = Object.keys(BUILD) as MiscName[];

export function miscSize(name: MiscName): { w: number; h: number } {
  const b = BUILD[name];
  return { w: b.w, h: b.h };
}

export function miscSvg(name: MiscName, theme: ArtTheme, prefix = `m${name.replace(/[^a-z0-9]/g, '')}${theme[0]}`): string {
  const b = BUILD[name];
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${b.w} ${b.h}" width="${b.w}" height="${b.h}">${b.b(ART_PAL[theme], prefix)}</svg>`;
}

export const miscUri = (name: MiscName, theme: ArtTheme) => 'data:image/svg+xml;utf8,' + encodeURIComponent(miscSvg(name, theme));
